import { useState } from "react";

const HOTEL_FORM_URL = "https://readdy.ai/api/form/d79bif32hsar0k5m0lhg";

const hotelFeatures = [
  {
    icon: "ri-hotel-bed-line",
    title: "Limpieza de Habitaciones",
    desc: "Limpieza completa y sanitización de habitaciones entre huéspedes. Cambio de ropa de cama, baños impecables y reposición de amenities.",
  },
  {
    icon: "ri-building-4-line",
    title: "Áreas Comunes",
    desc: "Lobby, pasillos, elevadores, salas de reuniones y áreas de descanso siempre presentables para tus huéspedes.",
  },
  {
    icon: "ri-restaurant-line",
    title: "Cocinas y Restaurantes",
    desc: "Limpieza profunda de cocinas industriales, comedores y áreas de servicio cumpliendo estándares de higiene.",
  },
  {
    icon: "ri-calendar-schedule-line",
    title: "Turnos Flexibles",
    desc: "Nos adaptamos a los horarios de check-in y check-out. Disponibles 7 días a la semana en los turnos que necesites.",
  },
  {
    icon: "ri-team-line",
    title: "Personal Especializado",
    desc: "Equipo entrenado específicamente en protocolos de limpieza hotelera. Discretos, uniformados y profesionales.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Estándares de Calidad",
    desc: "Seguimos los protocolos de higiene más exigentes del sector hotelero. Inspección de calidad en cada habitación.",
  },
];


export default function HotelSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch(HOTEL_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="hoteles" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-amber-600 text-sm font-semibold tracking-widest uppercase mb-3">
            Sector Hotelero
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Limpieza profesional
            <span className="text-amber-600"> para hoteles</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Contamos con personal especializado en limpieza hotelera. Mantenemos tus habitaciones y áreas comunes impecables para que tus huéspedes vivan la mejor experiencia.
          </p>
        </div>

        {/* Hero Banner */}
        <div className="relative rounded-3xl overflow-hidden mb-16 h-[340px] md:h-[420px]">
          <img
            src="https://public.readdy.ai/ai/img_res/46a98b753b9af42e9187427908fe4796.jpg"
            alt="Servicio de limpieza hotelera profesional"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-8 md:px-16">
            <div className="max-w-lg">
              <div className="inline-flex items-center gap-2 bg-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4 uppercase tracking-wide">
                <i className="ri-hotel-line"></i>
                Servicio B2B para Hoteles
              </div>
              <h3 className="text-2xl md:text-4xl font-bold text-white mb-4 leading-tight">
                Tu hotel siempre listo para el próximo huésped
              </h3>
              <p className="text-white/80 text-sm md:text-base leading-relaxed mb-6">
                Personal entrenado en protocolos hoteleros, disponible los 7 días de la semana. Nos integramos a tu operación sin interrupciones.
              </p>
              <a
                href="#cotizacion-hotel"
                className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Solicitar cotización
              </a>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {hotelFeatures.map((feat, i) => (
            <div key={i} className="flex gap-4 p-6 rounded-2xl bg-gray-50 hover:bg-amber-50 transition-colors group">
              <div className="w-12 h-12 flex items-center justify-center bg-amber-100 group-hover:bg-amber-200 rounded-xl shrink-0 transition-colors">
                <i className={`${feat.icon} text-amber-600 text-xl`}></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{feat.title}</h4>
                <p className="text-xs text-gray-500 leading-relaxed">{feat.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* How we work with hotels */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8 md:p-12 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="text-amber-600 text-sm font-semibold uppercase tracking-widest">Así trabajamos</span>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-6">
                Integración perfecta con tu operación hotelera
              </h3>
              <div className="flex flex-col gap-5">
                {[
                  { icon: "ri-phone-line", title: "Contacto inicial", desc: "Nos reunimos contigo para entender las necesidades específicas de tu hotel, número de habitaciones y turnos requeridos." },
                  { icon: "ri-file-list-3-line", title: "Propuesta personalizada", desc: "Elaboramos un plan de limpieza a medida con precios por habitación, frecuencia y servicios adicionales." },
                  { icon: "ri-user-star-line", title: "Asignación de equipo", desc: "Designamos personal fijo para tu hotel, entrenado en tus estándares y protocolos específicos." },
                  { icon: "ri-refresh-line", title: "Servicio continuo", desc: "Operamos de forma continua con supervisión de calidad, reportes y comunicación directa con tu gerencia." },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-500 rounded-xl shrink-0">
                      <i className={`${step.icon} text-white text-base`}></i>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">{step.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5 leading-relaxed">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-[320px] md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=hotel%20housekeeping%20staff%20professional%20uniform%20making%20bed%20white%20linens%20luxury%20hotel%20room%20organized%20clean%20towels%20bathroom%20amenities%20elegant%20interior%20warm%20light&width=600&height=400&seq=202&orientation=landscape"
                alt="Personal de limpieza hotelera profesional"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Quote Form */}
        <div id="cotizacion-hotel" className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left info */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                Solicita tu cotización
                <span className="text-amber-600"> sin costo</span>
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Cuéntanos sobre tu hotel y te enviamos una propuesta personalizada en menos de 24 horas. Sin compromisos.
              </p>
            </div>

            {/* Benefits */}
            <div className="bg-amber-50 rounded-2xl p-6 flex flex-col gap-3">
              <h4 className="font-bold text-gray-900 text-sm mb-1">¿Por qué elegirnos?</h4>
              {[
                "Personal verificado con antecedentes limpios",
                "Uniformados e identificados en todo momento",
                "Seguro de responsabilidad civil incluido",
                "Supervisión de calidad en cada turno",
                "Reemplazo inmediato ante ausencias",
                "Contrato flexible sin penalidades",
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <i className="ri-check-line text-amber-500 shrink-0"></i>
                  {item}
                </div>
              ))}
            </div>

            {/* Contact direct */}
            <a
              href="https://wa.me/19046087529?text=Hola%2C%20me%20interesa%20el%20servicio%20de%20limpieza%20para%20mi%20hotel"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-4 p-5 rounded-2xl bg-green-50 hover:bg-green-100 transition-colors cursor-pointer group"
            >
              <div className="w-11 h-11 flex items-center justify-center bg-green-500 rounded-xl shrink-0 group-hover:scale-105 transition-transform">
                <i className="ri-whatsapp-line text-white text-xl"></i>
              </div>
              <div>
                <div className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-0.5">¿Prefieres hablar directo?</div>
                <div className="font-bold text-gray-900 text-sm">Escríbenos por WhatsApp</div>
                <div className="text-xs text-gray-500">Respuesta inmediata</div>
              </div>
              <i className="ri-arrow-right-line text-green-400 ml-auto"></i>
            </a>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="flex flex-col items-center justify-center text-center py-20 bg-amber-50 rounded-2xl">
                <div className="w-20 h-20 flex items-center justify-center bg-amber-500 rounded-full mb-5">
                  <i className="ri-hotel-line text-white text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Solicitud recibida!</h3>
                <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
                  Gracias por contactarnos. Un representante de JEK Cleaning se comunicará con tu hotel en menos de <strong className="text-gray-700">24 horas</strong> con una propuesta personalizada.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-amber-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
                >
                  Enviar otra solicitud
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="hotel-quote-form"
                onSubmit={handleSubmit}
                className="bg-gray-50 rounded-2xl p-6 md:p-8 flex flex-col gap-5"
              >
                <h3 className="text-xl font-bold text-gray-900">Información de tu hotel</h3>

                {/* Hotel name & contact name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre del hotel *</label>
                    <input
                      type="text"
                      name="nombre_hotel"
                      required
                      placeholder="Ej: Hotel Grand Plaza"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre del contacto *</label>
                    <input
                      type="text"
                      name="nombre_contacto"
                      required
                      placeholder="Tu nombre"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white"
                    />
                  </div>
                </div>

                {/* Phone & Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Teléfono *</label>
                    <input
                      type="tel"
                      name="telefono"
                      required
                      placeholder="(904) 000-0000"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Correo electrónico *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="gerencia@hotel.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white"
                    />
                  </div>
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Dirección del hotel *</label>
                  <input
                    type="text"
                    name="direccion_hotel"
                    required
                    placeholder="Ej: 500 Ocean Blvd, Jacksonville, FL"
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white"
                  />
                </div>

                {/* Rooms & Frequency */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Número de habitaciones *</label>
                    <select
                      name="numero_habitaciones"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                    >
                      <option value="">Selecciona...</option>
                      <option value="1-20 habitaciones">1 – 20 habitaciones</option>
                      <option value="21-50 habitaciones">21 – 50 habitaciones</option>
                      <option value="51-100 habitaciones">51 – 100 habitaciones</option>
                      <option value="101-200 habitaciones">101 – 200 habitaciones</option>
                      <option value="Más de 200 habitaciones">Más de 200 habitaciones</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Frecuencia del servicio *</label>
                    <select
                      name="frecuencia"
                      required
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white cursor-pointer"
                    >
                      <option value="">Selecciona...</option>
                      <option value="Diario (7 días)">Diario (7 días)</option>
                      <option value="Diario (Lun-Vie)">Diario (Lun–Vie)</option>
                      <option value="Fines de semana">Fines de semana</option>
                      <option value="Según ocupación">Según ocupación</option>
                    </select>
                  </div>
                </div>

                {/* Services needed */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Servicios requeridos</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      "Limpieza de habitaciones",
                      "Áreas comunes",
                      "Cocina / Restaurante",
                      "Lavandería",
                      "Limpieza profunda",
                      "Desinfección",
                    ].map((srv) => (
                      <label key={srv} className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          name="servicios_requeridos"
                          value={srv}
                          className="accent-amber-500 cursor-pointer"
                        />
                        {srv}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Información adicional</label>
                  <textarea
                    name="notas"
                    rows={3}
                    maxLength={500}
                    placeholder="Horarios preferidos, requerimientos especiales, número de pisos, etc."
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-400 bg-white resize-none"
                    onChange={(e) => setCharCount(e.target.value.length)}
                  ></textarea>
                  <div className="text-xs text-gray-400 text-right mt-1">{charCount}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-base"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Enviando...</>
                  ) : (
                    <><i className="ri-hotel-line"></i> Solicitar cotización para mi hotel</>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center">
                  Cotización gratuita y sin compromiso. Te respondemos en menos de 24 horas.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
